// ============================================================
// TMR Portal Admin — Detalhe da empresa (rota /empresas/:companyId)
// ============================================================
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn, IconTile } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyableText } from '../../components/CopyableText';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { companyAddress, maskCNPJ } from '../../lib/format';
import { useCompany } from '../../data/companies/queries';
import { canOperate } from '../../data/companies/status';
import { useAwaitedContract } from '../../data/contracts/queries';
import { CompanyContractsTab } from '../contracts/CompanyContractsTab';
import { CompanyBusinessRulesTab } from '../businessRules/CompanyBusinessRulesTab';
import { CompanyAccountTab } from './CompanyAccountTab';
import { CompanyActivityTab } from './CompanyActivityTab';
import { CompanyCurrentAccountTab } from './CompanyCurrentAccountTab';
import { CompanyEditor } from './CompanyEditor';
import { CompanyOverview } from './CompanyOverview';
import { CompanyMapModal } from './CompanyMapModal';
import { CompanyRatesTab } from './CompanyRatesTab';
import { CompanyStatusControl } from './CompanyStatusControl';
import { OnboardingStatusPanel } from './OnboardingStatus';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';

/**
 * The Empresa's sections.
 *
 * `account` and `rates` are sections rather than cards on `overview` because each is its own subject with
 * its own actions — where the money goes, and what the TMR charges — and neither fits the read-only shape
 * of a cadastre card. Taxas in particular has two scopes, installment bands, a temporary-rate cycle and a
 * reconciliation with the legacy (`08`).
 */
type Tab = 'overview' | 'account' | 'current-account' | 'rates' | 'business-rules' | 'contracts' | 'activity';

export function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('overview');
  const [showMap, setShowMap] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  // The navigation the operator attempted while holding unsaved changes, held back until they confirm.
  const [pendingExit, setPendingExit] = useState<{ action: () => void } | null>(null);
  const [creatingContract, setCreatingContract] = useState(false);
  const [creatingRate, setCreatingRate] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const canUpdate = usePermission(PERMISSIONS.companyUpdate);
  const canManageContracts = usePermission(PERMISSIONS.contractsManage);
  const canManageRates = usePermission(PERMISSIONS.companyRatesManage);
  const canManageFinance = usePermission(PERMISSIONS.financeManage);

  // Every in-app way out of an unsaved edit — the discard button, the back link, the tabs,
  // "Novo contrato" — runs through here: with pending changes it asks first, otherwise it just goes.
  const leaveEditing = (action: () => void) => {
    if (editing && editorDirty) setPendingExit({ action });
    else action();
  };

  // Switching tabs leaves the editor behind, so it closes edit mode — guarded like any other exit.
  const goToTab = (value: Tab) => {
    if (value === tab) return;
    leaveEditing(() => {
      setEditing(false);
      setTab(value);
    });
  };

  // `?editar=1` opens the cadastre straight into edit mode, so a screen that found something wrong with the
  // Empresa can send the operator to the field instead of to a read-only page they then have to hunt around.
  // The parameter is consumed on arrival: it describes an intent, not a state, and leaving it in the URL
  // would reopen the editor on every refresh — including after the operator deliberately discarded.
  const wantsEditor = searchParams.get('editar') === '1';
  useEffect(() => {
    if (!wantsEditor) return;
    if (canUpdate) {
      setTab('overview');
      setEditing(true);
    }
    searchParams.delete('editar');
    setSearchParams(searchParams, { replace: true });
  }, [wantsEditor, canUpdate, searchParams, setSearchParams]);

  // Covers the exits a BrowserRouter cannot intercept: refresh, closing the tab, an external URL.
  // In-app navigations are guarded by `leaveEditing`; this is the browser's own last-chance prompt.
  useEffect(() => {
    if (!(editing && editorDirty)) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editing, editorDirty]);

  const query = useCompany(companyId);
  // The tab's count, and the wait for the contract the backend draws up on its own moments after an Empresa
  // is registered (03 §12). The tab reads its own page; this is the smallest page the server will serve,
  // read for its total, exactly as the Empresas list counts its statuses.
  const contract = useAwaitedContract(query.data);

  const backToList = (
    <Btn variant="secondary" icon={Icons.chevronL} onClick={() => navigate('/empresas')}>
      Voltar para Empresas
    </Btn>
  );

  if (query.isPending) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <Skeleton rows={7} />
      </div>
    );
  }

  if (query.isError) {
    // The backend answers 404 both for an unknown id and for an Empresa outside the operator's scope,
    // on purpose (06 §6) — so this wording must not imply the Empresa exists.
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        {notFound ? (
          <EmptyState message="Empresa não encontrada." hint="Ela não existe ou está fora do seu escopo." action={backToList} />
        ) : (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        )}
      </div>
    );
  }

  const company = query.data;
  const address = companyAddress(company.address);
  const tabs: { value: Tab; label: string; count?: number; pending?: boolean }[] = [
    { value: 'overview', label: 'Visão geral' },
    // "Conta bancária", not "Conta": "Conta Corrente" below is the Empresa's ledger account, a different
    // concept entirely, and two near-homonyms would be worse than a longer label. Keeping them adjacent is
    // deliberate — the legacy holds both on one producer row and confuses them constantly (14 §1).
    { value: 'account', label: 'Conta bancária' },
    { value: 'current-account', label: 'Conta Corrente' },
    { value: 'rates', label: 'Taxas' },
    // Right after Taxas: how this Empresa may sell, next to what it is charged — the TMR's default and, when
    // it negotiated one, its own value (`25` §9).
    { value: 'business-rules', label: 'Regras de negócio' },
    // While the automatic contract is on its way the badge says so instead of showing a zero that is about
    // to stop being true — the operator sees it is coming without having to open the tab.
    { value: 'contracts', label: 'Contratos', count: contract.total, pending: contract.awaiting },
    // Last, and without a count: the number would be the size of everything that ever happened, which grows
    // forever and says nothing an operator can act on. The counts that do mean something are the per-category
    // badges inside the tab (`07` §3).
    { value: 'activity', label: 'Histórico' },
  ];
  // The Empresa as a contract needs it: the party, and the representative who signs.
  const contractCompany = {
    id: company.id,
    name: company.name,
    cnpj: company.cnpj ?? '',
    legacyProducerId: company.legacyProducerId,
    representative: {
      name: company.representative?.name ?? null,
      email: company.representative?.email ?? null,
      phone: company.representative?.phone ?? null,
    },
    active: canOperate(company.status),
  };

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button
        type="button"
        onClick={() => leaveEditing(() => navigate('/empresas'))}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'var(--font-sans)', fontSize: 13.5, padding: 0 }}
      >
        <StrokeIcon d={Icons.chevronL} size={16} /> Empresas
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {address.precision !== 'none' ? (
            <button type="button" className="pa-map-tile" title="Ver no mapa" onClick={() => setShowMap(true)}>
              <IconTile size={56} radius={16}>
                <StrokeIcon d={Icons.clinic} size={26} />
              </IconTile>
            </button>
          ) : (
            <IconTile size={56} radius={16}>
              <StrokeIcon d={Icons.clinic} size={26} />
            </IconTile>
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 26, letterSpacing: '-0.01em', margin: 0, lineHeight: 1.05 }}>{company.name}</h1>
              {/* The business status leads: it is what the operator works against. Replication has its own
                  callout below, which is also where it can be acted on. */}
              <CompanyStatusControl companyId={company.id} status={company.status} />
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', marginTop: 4 }}>
              {company.cnpj ? (
                <CopyableText value={maskCNPJ(company.cnpj)} style={{ fontFamily: 'var(--font-mono)' }} />
              ) : (
                <span style={{ color: 'var(--fg-subtle)' }}>Sem CNPJ</span>
              )}{' '}·{' '}
              {company.address.city}/{company.address.state}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* One primary action per tab, in one slot: the cadastre is edited from Visão geral, contracts are
              drawn up from Contratos. Before, "Novo contrato" sat here on both tabs and the Contratos tab
              carried its own copy of the same button a few pixels below it. */}
          {tab === 'overview' && canUpdate && !editing && (
            <Btn variant="secondary" icon={Icons.pen} onClick={() => setEditing(true)}>
              Editar cadastro
            </Btn>
          )}
          {tab === 'account' && canManageFinance && (
            <Btn variant="primary" icon={Icons.plus} onClick={() => setCreatingAccount(true)}>
              Nova conta
            </Btn>
          )}
          {tab === 'rates' && canManageRates && (
            <Btn variant="primary" icon={Icons.plus} onClick={() => setCreatingRate(true)}>
              Nova taxa
            </Btn>
          )}
          {/* A switched-off Empresa may not take a contract — the server refuses it, so offering the
              button would only produce a modal that ends in a 409. It is also withheld while the automatic
              contract is on its way: drawing one up by hand at that moment is how the Empresa ends up with
              two, and it is the rework this wait was built to prevent. */}
          {tab === 'contracts' && canManageContracts && canOperate(company.status) && !contract.awaiting && (
            <Btn variant="primary" icon={Icons.plus} onClick={() => setCreatingContract(true)}>
              Criar contrato
            </Btn>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-default)' }}>
        {tabs.map((t) => {
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => goToTab(t.value)}
              style={{ position: 'relative', background: 'transparent', border: 0, cursor: 'pointer', padding: '10px 14px', fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: active ? 600 : 450, color: active ? 'var(--ink-900)' : 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              {t.label}
              {(t.pending || t.count != null) && (
                <span
                  className={t.pending ? 'pa-pulse' : undefined}
                  title={t.pending ? 'O contrato está sendo criado' : undefined}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '1px 6px', borderRadius: 999, background: active ? 'var(--forest-900)' : 'var(--cream-200)', color: active ? 'var(--cream-50)' : 'var(--fg-subtle)' }}
                >
                  {t.pending ? '…' : t.count}
                </span>
              )}
              {active && <span style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, background: 'var(--forest-900)', borderRadius: 2 }} />}
            </button>
          );
        })}
      </div>

      {tab === 'overview' &&
        (editing ? (
          <CompanyEditor
            company={company}
            onCancel={() => leaveEditing(() => setEditing(false))}
            onSaved={() => setEditing(false)}
            onDirtyChange={setEditorDirty}
          />
        ) : (
          <>
            <OnboardingStatusPanel companyId={company.id} />
            <CompanyOverview company={company} />
          </>
        ))}
      {tab === 'account' && (
        <CompanyAccountTab
          companyId={company.id}
          creating={creatingAccount}
          onCreatingChange={setCreatingAccount}
        />
      )}
      {tab === 'current-account' && (
        <CompanyCurrentAccountTab companyId={company.id} onGoToContracts={() => goToTab('contracts')} />
      )}
      {tab === 'rates' && (
        <CompanyRatesTab
          companyId={company.id}
          creating={creatingRate}
          onCreatingChange={setCreatingRate}
        />
      )}
      {tab === 'business-rules' && <CompanyBusinessRulesTab companyId={company.id} />}
      {tab === 'contracts' && (
        <CompanyContractsTab
          company={contractCompany}
          awaited={contract}
          creating={creatingContract}
          onCreatingChange={setCreatingContract}
        />
      )}
      {tab === 'activity' && <CompanyActivityTab companyId={company.id} />}

      {showMap && (
        <CompanyMapModal name={company.name} address={company.address} onClose={() => setShowMap(false)} />
      )}

      {pendingExit && (
        <ConfirmDialog
          title="Descartar alterações?"
          message="Há alterações não salvas neste cadastro. Se sair da edição agora, elas serão perdidas."
          confirmLabel="Descartar alterações"
          cancelLabel="Continuar editando"
          danger
          icon={Icons.alert}
          onConfirm={() => {
            setEditorDirty(false);
            pendingExit.action();
          }}
          onClose={() => setPendingExit(null)}
        />
      )}
    </div>
  );
}
